import { Editor, Extension } from '@tiptap/core'
import { isChangeOrigin } from '@tiptap/extension-collaboration'
import { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { Plugin, PluginKey, Selection } from '@tiptap/pm/state'
import { RESTRICTED_SECTION_NODE_NAME } from '../../../../../../backend/src/tiptap/editor/extensions/nodes/restricted-section'
import { MINUTE_NODE_INTERNAL_TYPE, MINUTE_TREE_INTERNAL_TYPE } from '../../../../../../backend/src/tiptap/editor/extensions/shared/minute-internal-types'

function internalTypeOf(node: ProseMirrorNode): unknown {
  return node.attrs['internalType']
}

function childrenOf(node: ProseMirrorNode): ProseMirrorNode[] {
  const children: ProseMirrorNode[] = []
  node.forEach((child) => children.push(child))
  return children
}

/** Each restricted item's mount, whole: its (hidden) title paragraph and the section it stands in for. */
function describeRestrictedMounts(doc: ProseMirrorNode): string {
  const mounts: string[] = []
  doc.descendants((node) => {
    if (node.type.name !== 'listItem') {
      return true
    }
    if (childrenOf(node).some((child) => child.type.name === RESTRICTED_SECTION_NODE_NAME)) {
      mounts.push(JSON.stringify(node.toJSON()))
    }
    return true
  })
  return JSON.stringify(mounts.sort())
}

type AgendaItemOutline = { nodeId: unknown; parentNodeId: unknown; title: unknown }

function isAgendaItem(node: ProseMirrorNode): boolean {
  return internalTypeOf(node) === MINUTE_NODE_INTERNAL_TYPE
}

/**
 * Every agenda item — a titled entry at any depth — in document order: its id, the item it sits under, and its title
 * paragraph (only that it is one, or, with `includeTitles`, its text too). The tree's top level must hold nothing but
 * agenda items. Content is left out entirely, so content can be added, removed or restructured freely, while no
 * agenda item can be removed, added, moved or lose its title.
 */
function describeAgendaOutline(doc: ProseMirrorNode, includeTitles: boolean): string {
  const items: AgendaItemOutline[] = []
  const topLevelEntries: string[] = []
  const collect = (node: ProseMirrorNode, parentNodeId: unknown): void =>
    node.forEach((child) => {
      if (isAgendaItem(child)) {
        const title = child.firstChild
        items.push({
          nodeId: child.attrs['nodeId'],
          parentNodeId,
          title: title === null ? null : includeTitles ? title.toJSON() : title.type.name,
        })
        collect(child, child.attrs['nodeId'])
      } else {
        collect(child, parentNodeId)
      }
    })

  doc.descendants((node) => {
    if (internalTypeOf(node) !== MINUTE_TREE_INTERNAL_TYPE) {
      return true
    }
    node.forEach((entry) => topLevelEntries.push(isAgendaItem(entry) ? 'item' : entry.type.name))
    collect(node, null)
    return false
  })
  return JSON.stringify({ topLevelEntries, items })
}

/**
 * Refuses any local edit that changes what `describe` reports, so that part of the document cannot be deleted, added
 * to or rewritten from this editor. Synced changes always pass: the server and other editors are not held to it.
 */
function protectStructure(name: string, describe: (doc: ProseMirrorNode) => string) {
  return Extension.create({
    name,

    addProseMirrorPlugins() {
      return [
        new Plugin({
          key: new PluginKey(name),
          filterTransaction: (transaction, state) =>
            !transaction.docChanged || isChangeOrigin(transaction) || describe(state.doc) === describe(transaction.doc),
        }),
      ]
    },
  })
}

/** Keeps a full user from deleting a restricted item's mount, including the hidden title paragraph it carries. */
export const ProtectRestrictedMounts = protectStructure('protectRestrictedMounts', describeRestrictedMounts)

/**
 * Backspace in an empty content item removes the item. The list's own handling would instead lift it a level, and from
 * the top of an agenda item that means out to the agenda's level, which the agenda guards refuse — leaving backspace
 * doing nothing. Agenda items are never removed this way.
 */
function removeEmptyContentItem(editor: Editor): boolean {
  const { $from, empty } = editor.state.selection
  if (!empty || $from.parent.content.size > 0 || $from.depth < 2) {
    return false
  }
  const item = $from.node($from.depth - 1)
  if (item.type.name !== 'listItem' || isAgendaItem(item) || item.childCount !== 1) {
    return false
  }

  const from = $from.before($from.depth - 1)
  const to = $from.after($from.depth - 1)
  // The end of whatever precedes the item, found before it goes, so the cursor lands there rather than on what follows.
  const previousEnd = Selection.near(editor.state.doc.resolve(from), -1).from
  return editor.commands.command(({ tr }) => {
    tr.deleteRange(from, to)
    tr.setSelection(Selection.near(tr.doc.resolve(tr.mapping.map(previousEnd, -1)), -1)).scrollIntoView()
    return true
  })
}

function protectAgendaStructure(name: string, includeTitles: boolean) {
  return protectStructure(name, (doc) => describeAgendaOutline(doc, includeTitles)).extend({
    priority: 1000,

    addKeyboardShortcuts() {
      return { Backspace: ({ editor }: { editor: Editor }) => removeEmptyContentItem(editor) }
    },
  })
}

/**
 * Keeps a scope document's agenda items — the restricted item and its subitems, the titled entries — in place: none
 * can be removed, added or moved, nothing can be raised to the top level, and each keeps its title paragraph, though
 * the title's text may change. The content under them stays free. Every scope editor has it, so a restricted item
 * cannot be deleted.
 */
export const ProtectAgendaItems = protectAgendaStructure('protectAgendaItems', false)

/**
 * Keeps a Restricted user to the content of the items shared with them: on top of {@link ProtectAgendaItems}, no
 * agenda item can be retitled.
 */
export const ProtectAgendaOutline = protectAgendaStructure('protectAgendaOutline', true)
